<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :maxBlocksInCache="null"
        >
            <div class="toolbar">
                <b-button-group :style="{width: '145px'}">
                    <form-square-switch
                        v-model="displayDisabledOwners"
                        :sync="true"
                        :labels="{checked: 'Show Disabled', unchecked: 'Hide Disabled'}"
                        :is-toolbar="true"
                    ></form-square-switch>
                </b-button-group>
                <b-button-group aria-label="Export table data">
                    <responsive-button
                        :icon="['fas', 'file-excel']"
                        :href="exportExcelHref"
                        @click="generatingExcelReportSuccessToast"
                        variant="primary"
                    ></responsive-button>
                </b-button-group>
                   <responsive-button
                    :icon="['fas', 'arrows-rotate']"
                    @click="refresh"
                    variant="primary"
                >Refresh</responsive-button>
                  <responsive-button
                    :icon="['fas', 'plus']"
                    variant="primary"
                    @click="handleCreateOwnerClick"
                >Add Owner</responsive-button>
            </div>
        </grid>
    </page>
</template>

<script>
    import has from 'lodash/has';
    import API from '../../../api';
    import FiltersStorage from '../../../utils/filters-storage';
    import { FILTERS_STORAGE_OWNER_LIST_ID } from '../../../constants';
    import Grid from '../../generic/grid/grid.vue';
    import { escapeHTML } from '../../../utils/strings'
    import Page from '../../generic/page/page.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';

    export default {
        name: 'OwnerList',
        mixins: [removeScrollMixin],
        components: {
            Grid,
            Page,
            FormSquareSwitch,
            ResponsiveButton,
            actionsCellRenderer: ActionsCellRenderer,
        },
        computed: {
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get('/app/owner/owner/', {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            only_active: !this.displayDisabledOwners,
                        }
                    });
                };
            },
            exportExcelHref() {
                return `/app/owner/owner/excel/?offset=${this.offset}&limit=${this.limit}` +
                    `&sort=${this.sort}&desc=${this.desc}&search=${this.search}` +
                    `&only_active=${!this.displayDisabledOwners}`;
            },
        },
        mounted() {
            (async () => {
                this.busy = true;

                this.loadFilters();
                try {
                    await this.fetchMetadata();
                } finally {
                    this.busy = false;
                }
            })();
        },
        data() {
            return {
                busy: false,
                displayDisabledOwners: true,
                tabs: [
                    {
                        'title': 'Owner List',
                        'route': '/app/owner/list/',
                        'active': true
                    },
                    {
                        'title': 'Logs',
                        'route': '/app/owner/list/logs/',
                        'active': false
                    }
                ],
                columnDefs: [
                    {
                        field: 'id',
                        type: 'idColumn',
                        maxWidth: 80,
                    },
                    {
                        field: 'first_name',
                        headerName: 'First Name',
                    },
                    {
                        field: 'last_name',
                        headerName: 'Last Name',
                    },
                    {
                        field: 'login_email',
                        headerName: 'Email',
                    },
                    {
                        field: 'phone',
                        headerName: 'Phone',
                    },
                    {
                        type: 'switchColumn',
                        field: 'is_active',
                        headerName: 'Active',
                        minWidth: 100,
                        maxWidth: 100,
                        cellRendererParams: {
                            msgOn: 'Are you sure you want to activate this owner?',
                            msgOff: 'Are you sure you want to deactivate this owner?',
                            onChange: async ({ node, api }, newValue) => {
                                const row = node.data;
                                const activate = newValue;
                                const activateDeactivateText = activate ? 'activate' : 'deactivate';

                                api.showLoadingOverlay();
                                try {
                                    await API.post(`/app/owner/owner/${row.id}/activate-deactivate/`, {
                                        activate
                                    })
                                } catch {
                                    this.errorToast({text: `Could not ${activateDeactivateText} owner.`})
                                    api.redrawRows({ rowNodes: [node] });
                                    return;
                                } finally {
                                    api.hideOverlay();
                                }
                            }
                        }
                    },
                    {
                        field: 'owner_properties',
                        headerName: 'Properties',
                        autoHeight: true,
                        sortable: false,
                        minWidth: 320,
                        cellRenderer: ({ data }) => {
                           return data.owner_properties_titles.map(item => {
                                return escapeHTML(item)
                            }).join('<br />')
                        }
                           
                    },
                    {
                        field: 'group',
                        headerName: 'Group',
                    },
                    {
                        headerName: '',
                        minWidth: 230,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                    },
                ],
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                search: '',
            }
        },
        methods: {
            handleCreateOwnerClick() {
                this.$router.push(`/app/owner/create/`);
            },
            saveFilters() {
                FiltersStorage.save(FILTERS_STORAGE_OWNER_LIST_ID, {
                    displayDisabledOwners: this.displayDisabledOwners,
                });
            },
            loadFilters() {
                const filters = FiltersStorage.load(FILTERS_STORAGE_OWNER_LIST_ID);

                if (has(filters, 'displayDisabledOwners')) {
                    this.displayDisabledOwners = filters.displayDisabledOwners;
                }
            },
            async fetchMetadata() {
                try {
                    await this.$store.dispatch('owner/fetchMetadata');
                } catch (err) {
                    this.errorToast({text: 'Could not load metadata'})
                }
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            }
        },
        watch: {
            displayDisabledOwners() {
                this.refresh();
                this.saveFilters();
            }
        }
    }
</script>

<style scoped lang="scss">
    .toolbar {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
</style>
